import { useRef, useState, type KeyboardEvent } from 'react'

import { Button } from '@/components/Button'

import { VisuallyHidden } from '@/components/VisuallyHidden'

import { TabContainerStyles, TabPanelStyles, TabsWrapper } from './styles'
import TabItem from './TabItem'
import { type Tabs } from './types'

interface Props {
  tabsConfig: Tabs[]
}

const Tabs = ({ tabsConfig }: Props) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const tabRefs = useRef<HTMLButtonElement[]>([])
  const [showTabDisclaimer, setShowTabDisclaimer] = useState(false)

  const setTabFocus = (index: number) => {
    const tab = tabRefs.current[index]
    if (tab) {
      tab.focus()
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const tabsLength = tabsConfig.length
    const eventTarget = event.currentTarget
    const currentIndex = tabRefs.current?.indexOf(eventTarget)

    const firstTab = () => setTabFocus(0)
    const lastTab = () => setTabFocus(tabsLength - 1)

    const nextTab = () => {
      if (currentIndex === tabsLength - 1) return firstTab()
      setTabFocus(currentIndex + 1)
    }

    const prevTab = () => {
      if (currentIndex === 0) return lastTab()
      setTabFocus(currentIndex - 1)
    }

    const keyMap = {
      ArrowRight: nextTab,
      ArrowLeft: prevTab,
      Home: firstTab,
      End: lastTab,
    }

    const action = keyMap[event.key as keyof typeof keyMap]
    if (action) {
      event.preventDefault()
      action()
    }
  }
  const handleTabSelection = (tabIndex: number) => {
    setShowTabDisclaimer(true)
    setSelectedTab(tabIndex)

    setTimeout(() => {
      setShowTabDisclaimer(false)
    }, 3000)
  }
  return (
    <>
      {tabsConfig && (
        <TabContainerStyles>
          <TabsWrapper className="tabs eu-sm-3" role="tablist" aria-orientation="horizontal">
            {tabsConfig.map((item, index) => (
              <Button
                key={`tab-${index}`}
                className={`tab__button ${selectedTab === index ? 'tab__button--current' : ''}`}
                size="lg"
                rounded
                modifier="solid"
                onClick={() => handleTabSelection(index)}
                role="tab"
                aria-controls={`tabpanel-id-${index}`}
                aria-selected={selectedTab === index}
                id={`tab-id-${index}`}
                ref={(element: HTMLButtonElement) => (tabRefs.current[index] = element)}
                onKeyDown={onKeyDown}
                tabIndex={selectedTab === index ? 0 : -1}
              >
                {item.label}
              </Button>
            ))}
          </TabsWrapper>

          {tabsConfig.map((tab, index) => (
            <section
              key={`tabpanel-${index}`}
              hidden={selectedTab !== index}
              role="tabpanel"
              aria-labelledby={`tab-id-${index}`}
              id={`tabpanel-id-${index}`}
              tabIndex={0}
            >
              <TabPanelStyles>
                {tab.items && tab.items.map((item, index) => <TabItem item={item} key={index} />)}
              </TabPanelStyles>
            </section>
          ))}

          <VisuallyHidden tabIndex={-1} aria-live="polite">
            {showTabDisclaimer && `Conteúdo de ${tabsConfig[selectedTab].label} carregado`}
          </VisuallyHidden>
        </TabContainerStyles>
      )}
    </>
  )
}

export default Tabs
